/**
 * useSubSessionOperations — Session and sub-session lifecycle operations.
 * Encapsulates start/pause/resume/cancel/complete mutations and handlers,
 * plus computed booleans for which actions are available.
 */
import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { SubSession } from '../../../../services/productionService';
import { isSessionCancellable } from '../../../../utils/sessionStatus';
import operatorService from '../../../../services/operatorService';
import stationManagementService from '../../../../services/stationManagementService';

interface UseSubSessionOperationsParams {
  sessionNumber: string;
  sessionStatus: string;
  subSessions: SubSession[];
  stationId?: number;
  onClose: () => void;
}

export function useSubSessionOperations({
  sessionNumber,
  sessionStatus,
  subSessions,
  stationId,
  onClose,
}: UseSubSessionOperationsParams) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // --- State ---
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeSubSessionId, setCompleteSubSessionId] = useState<string | null>(null);
  const [completeReason, setCompleteReason] = useState('');
  const [isCompletingSession, setIsCompletingSession] = useState(false);
  const [showCancelSubSessionConfirm, setShowCancelSubSessionConfirm] = useState(false);
  const [cancelSubSessionId, setCancelSubSessionId] = useState<string | null>(null);
  const [isCancellingSubSession, setIsCancellingSubSession] = useState(false);
  const [isStartingAll, setIsStartingAll] = useState(false);

  // --- Shared invalidation helper ---
  const invalidateSessionQueries = () => {
    queryClient.invalidateQueries(['productionSessions']);
    queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
    queryClient.invalidateQueries(['supervisor-stations']);
  };

  // --- Mutations (session-level, single sub-session) ---

  const startSessionMutation = useMutation(
    () => {
      const subSessionNumber = subSessions[0]?.sub_session_number;
      if (!subSessionNumber) throw new Error('No sub-session found');
      return operatorService.startSession(subSessionNumber, '', true);
    },
    {
      onSuccess: () => {
        invalidateSessionQueries();
        toast.success(t('sessionDetails.sessionStarted'));
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || error?.message || t('sessionDetails.failedToStart'));
      },
    }
  );

  const pauseSessionMutation = useMutation(
    (reason: string) => {
      const subSessionNumber = subSessions[0]?.sub_session_number;
      if (!subSessionNumber) throw new Error('No sub-session found');
      return operatorService.pauseSession(subSessionNumber, reason, true);
    },
    {
      onSuccess: () => {
        invalidateSessionQueries();
        toast.success(t('sessionDetails.sessionPaused'));
        setShowPauseModal(false);
        setPauseReason('');
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || error?.message || t('sessionDetails.failedToPause'));
      },
    }
  );

  const resumeSessionMutation = useMutation(
    () => {
      const subSessionNumber = subSessions[0]?.sub_session_number;
      if (!subSessionNumber) throw new Error('No sub-session found');
      return operatorService.resumeSession(subSessionNumber, undefined, true);
    },
    {
      onSuccess: () => {
        invalidateSessionQueries();
        toast.success(t('sessionDetails.sessionResumed'));
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || error?.message || t('sessionDetails.failedToResume'));
      },
    }
  );

  // Session cancellation lives in the shared <CancelSessionModal> (WCO-2706) — the modal owns
  // the reason dropdown, the password confirmation and the mutation.

  // --- Computed booleans ---

  const effectiveStatus = useMemo(() => sessionStatus, [sessionStatus]);
  const hasSingleSubSession = subSessions.length === 1;
  const hasMultipleSubSessions = subSessions.length > 1;
  const effectiveStationId = hasSingleSubSession ? subSessions[0].station_id : stationId;
  const showSessionLevelControls = !hasMultipleSubSessions;
  const isParentSessionTerminal = ['completed', 'cancelled'].includes(sessionStatus);
  const isActiveSession = ['active', 'in_progress'].includes(effectiveStatus);
  const isSessionControlLoading = startSessionMutation.isLoading || pauseSessionMutation.isLoading || resumeSessionMutation.isLoading;

  const canStartSession = showSessionLevelControls && ['received', 'ready'].includes(effectiveStatus) && !!effectiveStationId && !isParentSessionTerminal;
  const canPauseSession = showSessionLevelControls && ['active', 'in_progress'].includes(effectiveStatus) && !isParentSessionTerminal;
  const canResumeSession = showSessionLevelControls && effectiveStatus === 'paused' && !isParentSessionTerminal;
  // WCO-2706: running (active/paused) and finished sessions can no longer be cancelled
  const canCancelSession = isSessionCancellable(sessionStatus);

  // Per sub-session helpers
  const canStartSubSession = (ss: SubSession) => ['received', 'ready'].includes(ss.status) && !!ss.station_id;
  const canPauseSubSession = (ss: SubSession) => ['active', 'in_progress'].includes(ss.status);
  const canResumeSubSession = (ss: SubSession) => ss.status === 'paused';

  const getRemainingWeight = (ss: SubSession): number => {
    const initial = parseFloat(String(ss.initial_material_weight)) || 0;
    const output = parseFloat(String(ss.actual_output_weight)) || 0;
    const waste = parseFloat(String(ss.waste_weight)) || 0;
    const yieldFactor = parseFloat(String(ss.yield_percentage)) || 1;
    const usedWeight = yieldFactor > 0 ? (output / yieldFactor) + waste : output + waste;
    return Math.max(0, initial - usedWeight);
  };

  // --- Session-level handlers ---

  const handleStartSession = () => { startSessionMutation.mutate(); };

  const handlePauseSession = () => {
    if (!pauseReason) { toast.error(t('operator.providePauseReason')); return; }
    pauseSessionMutation.mutate(pauseReason);
  };

  const handleResumeSession = () => { resumeSessionMutation.mutate(); };

  // --- Sub-session handlers ---

  const handleStartSubSession = (subSessionNumber: string) => {
    operatorService.startSession(subSessionNumber, '', true)
      .then(() => { invalidateSessionQueries(); toast.success(t('sessionDetails.sessionStarted')); })
      .catch((error: any) => { toast.error(error?.response?.data?.error || error?.message || t('sessionDetails.failedToStart')); });
  };

  const handlePauseSubSession = (subSessionNumber: string) => {
    operatorService.pauseSession(subSessionNumber, 'supervisor_pause', true)
      .then(() => { invalidateSessionQueries(); toast.success(t('sessionDetails.sessionPaused')); })
      .catch((error: any) => { toast.error(error?.response?.data?.error || error?.message || t('sessionDetails.failedToPause')); });
  };

  const handleResumeSubSession = (subSessionNumber: string) => {
    operatorService.resumeSession(subSessionNumber, undefined, true)
      .then(() => { invalidateSessionQueries(); toast.success(t('sessionDetails.sessionResumed')); })
      .catch((error: any) => { toast.error(error?.response?.data?.error || error?.message || t('sessionDetails.failedToResume')); });
  };

  const handleCancelSubSession = (subSessionNumber: string) => {
    setCancelSubSessionId(subSessionNumber);
    setShowCancelSubSessionConfirm(true);
  };

  const confirmCancelSubSession = async () => {
    if (!cancelSubSessionId) return;
    setIsCancellingSubSession(true);
    try {
      const stationMgmt = await import('../../../../services/stationManagementService');
      await stationMgmt.default.cancelSubSession(cancelSubSessionId);
      queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
      queryClient.invalidateQueries(['productionSessions']);
      toast.success(t('sessionDetails.subSessionCancelled') || 'Sub-session cancelled');
      setShowCancelSubSessionConfirm(false);
      setCancelSubSessionId(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to cancel sub-session');
    } finally {
      setIsCancellingSubSession(false);
    }
  };

  const handleCompleteSubSession = async () => {
    if (!completeSubSessionId || !completeReason) {
      toast.error(t('sessionDetails.selectCompletionReason') || 'Please select a reason');
      return;
    }
    setIsCompletingSession(true);
    try {
      const subSession = subSessions.find(ss => ss.sub_session_number === completeSubSessionId);
      await operatorService.recordSupervisorOverride({
        override_type: 'session_completion',
        supervisor_id: 'supervisor',
        supervisor_name: 'Supervisor',
        sub_session_number: completeSubSessionId,
        reason: `Early completion - ${completeReason}`,
        details: {
          is_early_completion: true,
          early_completion_reason: completeReason,
          station_name: subSession?.station_name,
        },
      });
      const notes = `Early completion from supervisor dashboard. Reason: ${completeReason}`;
      await operatorService.completeSession(completeSubSessionId, notes, true, undefined, 'override');
      toast.success(t('sessionDetails.sessionCompleted') || 'Session completed');
      setShowCompleteModal(false);
      setCompleteSubSessionId(null);
      setCompleteReason('');
      invalidateSessionQueries();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'Failed to complete session');
    } finally {
      setIsCompletingSession(false);
    }
  };

  // Start all startable sub-sessions
  const startableSubSessions = subSessions.filter(ss => canStartSubSession(ss));
  const canStartAllSubSessions = hasMultipleSubSessions && startableSubSessions.length > 0 && !isParentSessionTerminal;

  const handleStartAllSubSessions = async () => {
    setIsStartingAll(true);
    let successCount = 0;
    let failCount = 0;
    for (const ss of startableSubSessions) {
      try {
        await operatorService.startSession(ss.sub_session_number, '', true);
        successCount++;
      } catch (error: any) {
        failCount++;
        console.error(`Failed to start sub-session ${ss.sub_session_number}:`, error);
      }
    }
    invalidateSessionQueries();
    if (failCount === 0) {
      toast.success(`Started ${successCount} sub-session${successCount > 1 ? 's' : ''}`);
    } else {
      toast.error(`Started ${successCount}, failed ${failCount} sub-session(s)`);
    }
    setIsStartingAll(false);
  };

  // StatusDropdown handler: routes each action to the appropriate flow
  const handleStatusChange = async (subSessionNumber: string, newStatus: string) => {
    const subSession = subSessions.find(ss => ss.sub_session_number === subSessionNumber);
    if (!subSession) return;

    try {
      if (newStatus === 'next') {
        // Force-promote via promote-next endpoint
        await stationManagementService.promoteSubSessionNext(subSessionNumber);
        invalidateSessionQueries();
        toast.success(t('sessionDetails.statusDropdown.promoteSuccess'));
        onClose();
      } else if (newStatus === 'active' && subSession.status === 'ready') {
        // Start the sub-session (existing flow — already closes modal on success)
        handleStartSubSession(subSessionNumber);
      } else if (newStatus === 'active' && subSession.status === 'paused') {
        // Resume the sub-session (existing flow — already closes modal on success)
        handleResumeSubSession(subSessionNumber);
      } else if (newStatus === 'paused') {
        // Route through existing pause flow
        handlePauseSubSession(subSessionNumber);
      } else if (newStatus === 'completed') {
        // Route through existing complete modal
        setCompleteSubSessionId(subSessionNumber);
        setShowCompleteModal(true);
      } else if (newStatus === 'cancelled') {
        // Route through existing cancel modal
        handleCancelSubSession(subSessionNumber);
      } else if (newStatus === 'received' && subSession.status === 'ready') {
        // Demote ready → planned (supervisor override — no conditions)
        await stationManagementService.updateSubSessionStatus(subSessionNumber, 'received');
        invalidateSessionQueries();
        toast.success(t('sessionDetails.statusDropdown.statusChanged'));
        onClose();
      } else if (newStatus === 'ready' && subSession.status === 'received') {
        // Promote planned → ready via status endpoint
        await stationManagementService.updateSubSessionStatus(subSessionNumber, 'ready');
        invalidateSessionQueries();
        toast.success(t('sessionDetails.statusDropdown.statusChanged'));
        onClose();
      } else {
        // Generic status change
        await stationManagementService.updateSubSessionStatus(subSessionNumber, newStatus);
        invalidateSessionQueries();
        toast.success(t('sessionDetails.statusDropdown.statusChanged'));
        onClose();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'Failed to change status');
    }
  };

  return {
    // Computed booleans
    effectiveStatus,
    hasSingleSubSession,
    hasMultipleSubSessions,
    effectiveStationId,
    showSessionLevelControls,
    isParentSessionTerminal,
    isActiveSession,
    isSessionControlLoading,
    canStartSession,
    canPauseSession,
    canResumeSession,
    canCancelSession,
    canStartSubSession,
    canPauseSubSession,
    canResumeSubSession,
    canStartAllSubSessions,
    isStartingAll,
    getRemainingWeight,

    // Session-level handlers
    handleStartSession,
    handlePauseSession,
    handleResumeSession,

    // Sub-session handlers
    handleStartSubSession,
    handlePauseSubSession,
    handleResumeSubSession,
    handleCancelSubSession,
    confirmCancelSubSession,
    handleCompleteSubSession,
    handleStartAllSubSessions,
    handleStatusChange,

    // Pause modal state
    showPauseModal, setShowPauseModal,
    pauseReason, setPauseReason,

    // Cancel sub-session modal state
    showCancelSubSessionConfirm, setShowCancelSubSessionConfirm,
    cancelSubSessionId, setCancelSubSessionId,
    isCancellingSubSession,

    // Complete modal state
    showCompleteModal, setShowCompleteModal,
    completeSubSessionId, setCompleteSubSessionId,
    completeReason, setCompleteReason,
    isCompletingSession,

    // Mutation states (for loading indicators)
    splitSessionMutationLoading: false, // placeholder — split mutation stays in parent
  };
}
